<template>
  <div class="card shift-page">
    <div class="card-body">
      <!-- Header -->
      <div class="shift-header">
        <div class="month-selector">
          <div class="year-selector">
            <button class="year-nav-btn" @click="changeYear(-1)"><i class="fas fa-chevron-left"></i></button>
            <span class="year-label">{{ currentDate.getFullYear() }}年</span>
            <button class="year-nav-btn" @click="changeYear(1)"><i class="fas fa-chevron-right"></i></button>
          </div>
          <div class="month-btn-group">
            <button v-for="m in 12" :key="m" :class="['month-btn', { active: currentDate.getMonth() === m - 1 }]" @click="selectMonth(m - 1)">{{ m }}月</button>
          </div>
        </div>
        <div class="shift-btn-group">
          <button v-if="shiftPatternType === 2" :class="['btn btn-sm', showRequestMode ? 'btn-warning' : 'btn-outline-warning']" @click="showRequestMode = !showRequestMode">
            <i class="fas fa-hand-paper mr-1"></i>休み希望
          </button>
          <button class="btn btn-outline-secondary btn-sm" @click="exportCSV">
            <i class="fas fa-file-csv mr-1"></i>CSV
          </button>
          <button class="btn btn-outline-secondary btn-sm" @click="exportExcel">
            <i class="fas fa-file-excel mr-1"></i>Excel
          </button>
          <button class="btn btn-success btn-sm" @click="openGenerateModal" :disabled="generating">
            <span v-if="generating" class="spinner-border spinner-border-sm mr-1"></span>
            <i v-else class="fas fa-magic mr-1"></i>シフト生成
          </button>
          <button class="btn btn-outline-danger btn-sm" @click="deleteMonth">
            <i class="fas fa-trash mr-1"></i>月削除
          </button>
          <button class="btn btn-outline-info btn-sm" @click="showPublicUrl">
            <i class="fas fa-link mr-1"></i>公開URL
          </button>
        </div>
      </div>

      <!-- Grid -->
      <div ref="gridContainer" class="shift-table-wrap" @click="onGridClick"></div>
      <div v-if="loading" class="text-center text-muted py-4">読み込み中...</div>
    </div>
  </div>

  <!-- Editor modal -->
  <div v-if="showEditor" class="modal-overlay" @click.self="showEditor = false">
    <div class="modal-box">
      <div class="modal-box-header">
        <h6 class="modal-box-title">{{ editName }} - {{ editDateDisplay }}</h6>
        <button type="button" class="modal-box-close" @click="showEditor = false">&times;</button>
      </div>
      <div class="modal-box-body">
        <div class="shift-type-btns">
          <button :class="['type-btn', { active: editType === 1 }]" @click="editType = 1">
            <i class="fas fa-briefcase mr-1"></i>出勤
          </button>
          <button :class="['type-btn half', { active: editType === 4 }]" @click="editType = 4">半休</button>
          <button :class="['type-btn off', { active: editType === 2 }]" @click="editType = 2">休</button>
          <button :class="['type-btn leave', { active: editType === 3 }]" @click="editType = 3">有給</button>
          <button :class="['type-btn half-leave', { active: editType === 5 }]" @click="editType = 5">半日有給</button>
        </div>
        <div v-show="editType === 1 || editType === 4 || editType === 5" class="mt-3">
          <div v-if="editPatterns.length > 1" class="field-group">
            <label>勤務パターン</label>
            <div class="pattern-select-btns">
              <button
                v-for="p in editPatterns"
                :key="p.pattern_name"
                :class="['pattern-sel-btn', { active: editSelectedPattern === p.pattern_name }]"
                @click="selectPattern(p)"
              >{{ p.pattern_name }}</button>
            </div>
          </div>
          <div class="edit-times">
            <div class="field-group">
              <label>開始</label>
              <input type="time" class="form-control form-control-sm" v-model="editStart">
            </div>
            <div class="field-group">
              <label>終了</label>
              <input type="time" class="form-control form-control-sm" v-model="editEnd">
            </div>
          </div>
          <div class="field-group mt-2">
            <label>就業場所</label>
            <div class="location-btns">
              <button :class="['loc-btn', { active: editLocation === 'office' }]" @click="editLocation = 'office'">
                <i class="fas fa-building mr-1"></i>会社
              </button>
              <button :class="['loc-btn', { active: editLocation === 'remote' }]" @click="editLocation = 'remote'">
                <i class="fas fa-home mr-1"></i>在宅
              </button>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-box-footer">
        <button type="button" class="btn btn-outline-secondary btn-sm" @click="showEditor = false">キャンセル</button>
        <button type="button" class="btn btn-primary btn-sm" @click="saveCell">保存</button>
      </div>
    </div>
  </div>

  <!-- Generate shift modal (staff selection) -->
  <div v-if="showGenerateModal" class="modal-overlay" @click.self="showGenerateModal = false">
    <div class="modal-box" style="width:480px;">
      <div class="modal-box-header">
        <h6 class="modal-box-title"><i class="fas fa-magic mr-2"></i>シフト自動生成</h6>
        <button type="button" class="modal-box-close" @click="showGenerateModal = false">&times;</button>
      </div>
      <div class="modal-box-body">
        <p style="font-size:13px;color:#4a5568;margin-bottom:10px;">生成対象のスタッフを選択してください。既存のシフトは上書きされます。</p>
        <div style="display:flex;gap:8px;margin-bottom:10px;">
          <button type="button" class="btn btn-outline-secondary btn-sm" @click="selectAllForGenerate">全選択</button>
          <button type="button" class="btn btn-outline-secondary btn-sm" @click="clearSelectForGenerate">全解除</button>
        </div>
        <div style="max-height:320px;overflow-y:auto;border:1px solid #e2e8f0;border-radius:6px;padding:8px;">
          <label v-for="s in staffList" :key="s.staff_id"
                 style="display:flex;align-items:center;gap:8px;padding:6px 4px;cursor:pointer;">
            <input type="checkbox" :value="s.staff_id" v-model="selectedGenerateStaffIds">
            <span style="font-size:13px;">{{ s.name }}</span>
          </label>
          <div v-if="!staffList.length" class="text-muted text-center py-2" style="font-size:12px;">スタッフが登録されていません</div>
        </div>
        <p style="font-size:11px;color:#718096;margin-top:8px;">選択: {{ selectedGenerateStaffIds.length }}名</p>
      </div>
      <div class="modal-box-footer">
        <button type="button" class="btn btn-outline-secondary btn-sm" @click="showGenerateModal = false">キャンセル</button>
        <button type="button" class="btn btn-success btn-sm" @click="confirmGenerate" :disabled="generating || !selectedGenerateStaffIds.length">
          <span v-if="generating" class="spinner-border spinner-border-sm mr-1"></span>
          <i v-else class="fas fa-magic mr-1"></i>生成
        </button>
      </div>
    </div>
  </div>

  <!-- Delete paid leave confirmation modal -->
  <div v-if="showDeleteLeaveModal" class="modal-overlay" @click.self="showDeleteLeaveModal = false">
    <div class="modal-box" style="width:420px;">
      <div class="modal-box-header">
        <h6 class="modal-box-title">有給申請を削除しますか？</h6>
        <button type="button" class="modal-box-close" @click="showDeleteLeaveModal = false">&times;</button>
      </div>
      <div class="modal-box-body">
        <div style="padding:12px 4px;font-size:13px;color:#4a5568;">
          <div style="margin-bottom:6px;"><strong>スタッフ:</strong> {{ deleteLeaveTarget.staffName }}</div>
          <div style="margin-bottom:6px;"><strong>日付:</strong> {{ deleteLeaveTarget.dateDisplay }}</div>
          <div style="margin-bottom:6px;"><strong>種別:</strong> {{ deleteLeaveTarget.leaveTypeLabel }}</div>
          <div style="margin-bottom:6px;"><strong>状態:</strong>
            <span :class="deleteLeaveTarget.statusClass">{{ deleteLeaveTarget.statusLabel }}</span>
          </div>
          <div v-if="deleteLeaveTarget.reason"><strong>理由:</strong> {{ deleteLeaveTarget.reason }}</div>
        </div>
        <p style="font-size:12px;color:#e53e3e;margin:8px 0 0 0;">※ 削除すると、申請一覧からも消えます。</p>
      </div>
      <div class="modal-box-footer">
        <button type="button" class="btn btn-outline-secondary btn-sm" @click="showDeleteLeaveModal = false">いいえ</button>
        <button type="button" class="btn btn-danger btn-sm" @click="confirmDeleteLeave">はい</button>
      </div>
    </div>
  </div>

  <!-- Public URL Modal -->
  <div v-if="showPublicUrlModal" class="modal-overlay" @click.self="showPublicUrlModal = false">
    <div class="modal-box" style="width:500px;">
      <div class="modal-box-header">
        <h6 style="font-weight:700;margin:0;"><i class="fas fa-link mr-2"></i>シフト表 公開URL</h6>
        <button class="close-btn" @click="showPublicUrlModal = false">&times;</button>
      </div>
      <div class="modal-box-body" style="padding:16px 20px;">
        <p style="font-size:13px;color:#4a5568;margin-bottom:12px;">このURLを共有すると、Basic認証付きでシフト表を閲覧できます。</p>
        <div style="display:flex;gap:8px;align-items:center;">
          <input type="text" class="form-control" :value="publicShiftUrl" readonly ref="publicUrlInput" style="font-size:13px;background:#f7fafc;">
          <button class="btn btn-primary btn-sm" @click="copyPublicUrl" style="white-space:nowrap;"><i class="fas fa-copy mr-1"></i>コピー</button>
        </div>
        <div style="margin-top:12px;padding:10px 14px;background:#f7fafc;border-radius:8px;border:1px solid #e2e8f0;">
          <p style="font-size:12px;color:#718096;margin:0;"><i class="fas fa-lock mr-1"></i>Basic認証で保護されています</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const STORAGE_KEY = 'shiftPage.currentDate';

function restoreCurrentDate() {
  try {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved) {
      const d = new Date(saved);
      if (!isNaN(d.getTime())) return new Date(d.getFullYear(), d.getMonth(), 1);
    }
  } catch (e) { /* ignore */ }
  return new Date(new Date().getFullYear(), new Date().getMonth(), 1);
}

export default {
  data() {
    return {
      currentDate: restoreCurrentDate(),
      days: [],
      staffList: [],
      loading: true,
      generating: false,
      // Editor
      showEditor: false,
      editStaffId: null,
      editName: '',
      editDate: '',
      editDateDisplay: '',
      editType: 1,
      editStart: '09:00',
      editEnd: '18:00',
      editLocation: 'office',
      editPatterns: [],
      editSelectedPattern: '',
      showRequestMode: false,
      shiftRequests: {},
      shiftPatternType: 1,
      showPublicUrlModal: false,
      publicShiftToken: '',
      // Generate modal
      showGenerateModal: false,
      selectedGenerateStaffIds: [],
      // Paid leave deletion modal
      showDeleteLeaveModal: false,
      deleteLeaveTarget: {
        paidLeaveId: null,
        staffName: '',
        dateDisplay: '',
        leaveTypeLabel: '',
        statusLabel: '',
        statusClass: '',
        reason: '',
      },
    }
  },
  computed: {
    publicShiftUrl() {
      if (!this.publicShiftToken) return '';
      const base = process.env.VUE_APP_API_PATH ? process.env.VUE_APP_API_PATH.replace(/\/api$/, '') : window.location.origin;
      return base + '/shared/shift/' + this.publicShiftToken;
    },
  },
  mounted() {
    this.loadShifts();
  },
  methods: {
    showPublicUrl() {
      const shop_id = this.$store.getters.shop;
      this.$http.get(process.env.VUE_APP_API_PATH + '/shop/shop/' + shop_id)
      .then(res => {
        this.publicShiftToken = res.data.data.public_shift_token || '';
        if (!this.publicShiftToken) {
          this.$toast.error('公開トークンが未設定です。マイグレーションを実行してください。');
          return;
        }
        this.showPublicUrlModal = true;
      })
      .catch(() => { this.$toast.error('店舗情報の取得に失敗しました'); });
    },
    copyPublicUrl() {
      const input = this.$refs.publicUrlInput;
      if (input) {
        input.select();
        navigator.clipboard.writeText(input.value).then(() => {
          this.$toast.success('URLをコピーしました');
        }).catch(() => {
          document.execCommand('copy');
          this.$toast.success('URLをコピーしました');
        });
      }
    },
    changeYear(dir) {
      this.currentDate = new Date(this.currentDate.getFullYear() + dir, this.currentDate.getMonth(), 1);
      this._persistCurrentDate();
      this.loadShifts();
    },
    selectMonth(m) {
      this.currentDate = new Date(this.currentDate.getFullYear(), m, 1);
      this._persistCurrentDate();
      this.loadShifts();
    },
    _persistCurrentDate() {
      try { sessionStorage.setItem(STORAGE_KEY, this.formatDate()); } catch (e) { /* ignore */ }
    },
    formatDate() {
      return this.currentDate.getFullYear() + '-' + String(this.currentDate.getMonth() + 1).padStart(2, '0') + '-01';
    },
    loadShifts() {
      this.loading = true;
      const shop_id = this.$store.getters.shop;
      this.$http.get(process.env.VUE_APP_API_PATH + '/shop/shift/' + shop_id + '/' + this.formatDate())
      .then(res => {
        this.days = res.data.data.days;
        this.staffList = res.data.data.staff;
        // Build request lookup: "staffId-date" => request
        const requests = {};
        (res.data.data.shift_requests || []).forEach(r => {
          requests[r.staff_id + '-' + r.date] = r;
        });
        this.shiftRequests = requests;
        this.shiftPatternType = res.data.data.shift_pattern_type || 1;
        this.renderGrid();
        this.loading = false;
      })
      .catch(e => {
        console.log(e);
        this.loading = false;
      });
    },

    renderGrid() {
      const container = this.$refs.gridContainer;
      if (!container) return;
      const days = this.days;
      const staff = this.staffList;
      const wn = ['日','月','火','水','木','金','土'];

      if (!days.length || !staff.length) {
        container.innerHTML = '';
        return;
      }

      // Name column wider for name + counts
      let html = '<div class="shift-grid" style="display:grid;grid-template-columns:140px repeat(' + days.length + ',minmax(52px,1fr));">';

      // Header
      html += '<div class="sg-cell sg-header sg-sticky">名前</div>';
      for (const d of days) {
        let cls = 'sg-cell sg-header';
        if (d.weekday === 0 || d.holiday) cls += ' sg-sun';
        else if (d.weekday === 6) cls += ' sg-sat';
        html += '<div class="' + cls + '"><div class="sg-dh"><span class="sg-dn">' + d.day + '</span><span class="sg-dw">' + wn[d.weekday] + '</span>';
        if (d.holiday) html += '<span class="sg-hol">祝</span>';
        html += '</div></div>';
      }

      // Staff rows
      for (const s of staff) {
        const eName = String(s.name).replace(/&/g,'&amp;').replace(/</g,'&lt;');

        // Count off days, paid leave, half days
        let offCount = 0;
        let leaveCount = 0;
        let halfCount = 0;
        let halfLeaveCount = 0;
        for (const d of days) {
          const sh = s.shifts[d.date];
          if (sh) {
            if (sh.shift_type === 2) offCount++;
            else if (sh.shift_type === 3) leaveCount++;
            else if (sh.shift_type === 4) halfCount++;
            else if (sh.shift_type === 5) halfLeaveCount++;
          } else {
            offCount++;
          }
        }

        const totalLeave = leaveCount + halfLeaveCount * 0.5;
        let badges = '';
        if (offCount > 0) badges += '<span class="sg-badge-off">休' + offCount + '</span>';
        if (halfCount > 0) badges += '<span class="sg-badge-half">半' + halfCount + '</span>';
        if (totalLeave > 0) badges += '<span class="sg-badge-leave">有' + totalLeave + '</span>';

        html += '<div class="sg-cell sg-sticky sg-name"><span class="sg-name-text">' + eName + '</span><span class="sg-name-badges">' + badges + '</span></div>';

        for (const d of days) {
          const shift = s.shifts[d.date];
          const t = shift ? shift.shift_type : 0;
          const reqKey = s.staff_id + '-' + d.date;
          const req = this.shiftRequests[reqKey];
          let cls = 'sg-cell sg-data';
          if (d.weekday === 0 || d.holiday) cls += ' sg-sun-bg';
          else if (d.weekday === 6) cls += ' sg-sat-bg';
          const isPending = shift && shift.approval_status === 1;
          if (t === 2) cls += ' sg-off-bg';
          else if (t === 3) cls += isPending ? ' sg-leave-pending-bg' : ' sg-leave-approved-bg';
          else if (t === 4) cls += ' sg-half-bg';
          else if (t === 5) cls += isPending ? ' sg-leave-pending-bg' : ' sg-leave-approved-bg';
          const isShiftPattern = this.shiftPatternType === 2;
          if (isShiftPattern && req && req.request_type === 1) cls += ' sg-req-off';

          // 基本勤務時間との比較
          const patterns = s.work_patterns || [];
          const defaultStart = patterns.length > 0 ? (patterns[0].start_time || '').slice(0, 5) : '';
          const defaultEnd = patterns.length > 0 ? (patterns[0].end_time || '').slice(0, 5) : '';
          const isRegular = shift && shift.start_time === defaultStart && shift.end_time === defaultEnd;
          const timeCls = isRegular ? 'sg-time-small' : 'sg-time-main';

          let inner = '';
          if (t === 1) {
            const loc = shift.location || 'office';
            const locTag = loc === 'remote' ? '<span class="sg-loc">在宅</span>' : '<span class="sg-loc sg-loc-spacer">&nbsp;</span>';
            inner = '<div class="sg-shift-content"><span class="' + timeCls + '">' + (shift.start_time || '') + '</span><span class="' + timeCls + '">' + (shift.end_time || '') + '</span>' + locTag + '</div>';
          } else if (t === 4) {
            const loc4 = shift.location || 'office';
            const locTag4 = loc4 === 'remote' ? '<span class="sg-loc">在宅</span>' : '<span class="sg-loc sg-loc-spacer">&nbsp;</span>';
            inner = '<div class="sg-shift-content"><span class="sg-type-label sg-type-half">半休</span><span class="sg-time-sub">' + (shift.start_time || '') + '-' + (shift.end_time || '') + '</span>' + locTag4 + '</div>';
          } else if (t === 5) {
            const loc5 = shift.location || 'office';
            const locTag5 = loc5 === 'remote' ? '<span class="sg-loc">在宅</span>' : '<span class="sg-loc sg-loc-spacer">&nbsp;</span>';
            inner = '<div class="sg-shift-content"><span class="sg-type-label sg-type-half-leave">半有給</span><span class="sg-time-sub">' + (shift.start_time || '') + '-' + (shift.end_time || '') + '</span>' + locTag5 + '</div>';
          } else if (t === 2) {
            inner = '<span class="sg-type-label sg-type-off">休</span>';
          } else if (t === 3) {
            const pendingMark = isPending ? '<span class="sg-pending-mark">待</span>' : '';
            const typeCls = isPending ? 'sg-type-label sg-type-paid-pending' : 'sg-type-label sg-type-paid-approved';
            inner = '<span class="' + typeCls + '">有給</span>' + pendingMark;
          }

          // 休み希望マーカー（シフトパターンのみ）
          if (isShiftPattern && req && req.request_type === 1) {
            inner += '<span class="sg-req-mark">希</span>';
          }

          html += '<div class="' + cls + '" data-sid="' + s.staff_id + '" data-date="' + d.date + '">' + inner + '</div>';
        }
      }

      // Count row
      html += '<div class="sg-cell sg-sticky sg-count-label">出勤数</div>';
      for (const d of days) {
        const count = staff.filter(s => { const sh = s.shifts[d.date]; return sh && (sh.shift_type === 1 || sh.shift_type === 4 || sh.shift_type === 5); }).length;
        html += '<div class="sg-cell sg-count">' + count + '</div>';
      }

      html += '</div>';
      container.innerHTML = html;
    },

    onGridClick(e) {
      const cell = e.target.closest('.sg-data');
      if (!cell) return;
      const staffId = parseInt(cell.dataset.sid);
      const date = cell.dataset.date;
      if (!staffId || !date) return;

      // 休み希望モード
      if (this.showRequestMode) {
        this.toggleRequest(staffId, date);
        return;
      }

      const staff = this.staffList.find(s => s.staff_id === staffId);
      if (!staff) return;

      // 有給セルをタップ時は削除確認モーダルを優先表示
      const clickedShift = staff.shifts[date];
      if (clickedShift && clickedShift.paid_leave_id) {
        const dd = new Date(date);
        this.deleteLeaveTarget = {
          paidLeaveId: clickedShift.paid_leave_id,
          staffName: staff.name,
          dateDisplay: (dd.getMonth() + 1) + '/' + dd.getDate(),
          leaveTypeLabel: clickedShift.leave_type === 2 ? '半日有給' : '全日有給',
          statusLabel: clickedShift.approval_status === 2 ? '承認済み' : '承認待ち',
          statusClass: clickedShift.approval_status === 2 ? 'badge-status-approved' : 'badge-status-pending',
          reason: clickedShift.leave_reason || '',
        };
        this.showDeleteLeaveModal = true;
        return;
      }

      this.editStaffId = staffId;
      this.editName = staff.name;
      this.editDate = date;
      const dd = new Date(date);
      this.editDateDisplay = (dd.getMonth() + 1) + '/' + dd.getDate();

      // Prepare work patterns for selection
      const patterns = (staff.work_patterns || []).map(p => ({
        pattern_name: p.pattern_name || '通常',
        start_time: (p.start_time || '').slice(0, 5) || '09:00',
        end_time: (p.end_time || '').slice(0, 5) || '18:00',
      }));
      this.editPatterns = patterns;

      const shift = staff.shifts[date];
      if (shift) {
        this.editType = shift.shift_type;
        this.editStart = shift.start_time || '09:00';
        this.editEnd = shift.end_time || '18:00';
        this.editLocation = shift.location || 'office';
        // Find matching pattern
        const matched = patterns.find(p => p.start_time === this.editStart && p.end_time === this.editEnd);
        this.editSelectedPattern = matched ? matched.pattern_name : '';
      } else {
        this.editType = 1;
        this.editLocation = 'office';
        if (patterns.length > 0) {
          this.editSelectedPattern = patterns[0].pattern_name;
          this.editStart = patterns[0].start_time;
          this.editEnd = patterns[0].end_time;
        } else {
          this.editSelectedPattern = '';
          this.editStart = '09:00';
          this.editEnd = '18:00';
        }
      }
      this.showEditor = true;
    },

    selectPattern(p) {
      this.editSelectedPattern = p.pattern_name;
      this.editStart = p.start_time;
      this.editEnd = p.end_time;
    },

    async confirmDeleteLeave() {
      const id = this.deleteLeaveTarget.paidLeaveId;
      if (!id) return;
      try {
        await this.$http.delete(process.env.VUE_APP_API_PATH + '/shop/paidLeave/' + id);
        this.$toast.success('有給申請を削除しました');
      } catch (e) {
        this.$toast.error('削除に失敗しました');
      } finally {
        this.showDeleteLeaveModal = false;
        this.loadShifts();
      }
    },

    saveCell() {
      const shop_id = this.$store.getters.shop;
      this.$http.post(process.env.VUE_APP_API_PATH + '/shop/shift/cell', {
        staff_id: this.editStaffId,
        shop_id: shop_id,
        date: this.editDate,
        shift_type: this.editType,
        start_time: [1, 4, 5].includes(this.editType) ? this.editStart : null,
        end_time: [1, 4, 5].includes(this.editType) ? this.editEnd : null,
        location: [1, 4, 5].includes(this.editType) ? this.editLocation : 'office',
      })
      .then(() => {
        this.showEditor = false;
        this.loadShifts();
      })
      .catch(e => {
        this.$toast.error('保存に失敗しました');
        console.log(e);
      });
    },

    exportCSV() {
      this.downloadFile('csv');
    },
    exportExcel() {
      this.downloadFile('excel');
    },
    downloadFile(type) {
      const shop_id = this.$store.getters.shop;
      this.$http.get(
        process.env.VUE_APP_API_PATH + '/shop/shift-export/' + type + '/' + shop_id + '/' + this.formatDate(),
        { responseType: 'blob' }
      )
      .then(response => {
        const ext = type === 'csv' ? 'csv' : 'xlsx';
        const blob = new Blob([response.data]);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'shift_' + this.formatDate().slice(0, 7) + '.' + ext;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      })
      .catch(e => {
        this.$toast.error('ダウンロードに失敗しました');
        console.log(e);
      });
    },
    toggleRequest(staffId, date) {
      const key = staffId + '-' + date;
      const existing = this.shiftRequests[key];
      const shop_id = this.$store.getters.shop;

      if (existing) {
        // Remove request
        this.$http.delete(process.env.VUE_APP_API_PATH + '/shop/shift-requests/' + existing.id)
        .then(() => {
          delete this.shiftRequests[key];
          this.renderGrid();
        })
        .catch(e => { console.log(e); });
      } else {
        // Add off request
        this.$http.post(process.env.VUE_APP_API_PATH + '/shop/shift-requests', {
          staff_id: staffId,
          shop_id: shop_id,
          date: date,
          request_type: 1,
        })
        .then(res => {
          this.shiftRequests[key] = {
            id: res.data.data.id,
            staff_id: staffId,
            date: date,
            request_type: 1,
          };
          this.renderGrid();
        })
        .catch(e => { console.log(e); });
      }
    },
    deleteMonth() {
      if (!confirm('この月のシフトを全て削除します。よろしいですか？')) return;
      const shop_id = this.$store.getters.shop;
      this.$http.delete(process.env.VUE_APP_API_PATH + '/shop/shift/month/' + shop_id + '/' + this.formatDate())
      .then(res => {
        this.$toast.success(res.data.message);
        this.loadShifts();
      })
      .catch(e => {
        this.$toast.error('削除に失敗しました');
        console.log(e);
      });
    },
    openGenerateModal() {
      if (this.generating) return;
      this.selectedGenerateStaffIds = this.staffList.map(s => s.staff_id);
      this.showGenerateModal = true;
    },
    selectAllForGenerate() {
      this.selectedGenerateStaffIds = this.staffList.map(s => s.staff_id);
    },
    clearSelectForGenerate() {
      this.selectedGenerateStaffIds = [];
    },
    confirmGenerate() {
      if (this.generating || !this.selectedGenerateStaffIds.length) return;
      this.generating = true;
      const shop_id = this.$store.getters.shop;
      this.$http.post(
        process.env.VUE_APP_API_PATH + '/shop/shift/generate/' + shop_id + '/' + this.formatDate(),
        { staff_ids: this.selectedGenerateStaffIds }
      )
      .then((res) => {
        const summary = res.data?.data?.summary;
        if (summary) {
          let msg = 'シフトを生成しました\n';
          summary.forEach(s => { msg += s.name + ': 出勤' + s.work_days + '日 / 休' + s.off_days + '日\n'; });
          alert(msg);
        } else {
          this.$toast.success('シフトを生成しました');
        }
        this.showGenerateModal = false;
        window.location.reload();
      })
      .catch(e => {
        this.$toast.error('生成に失敗しました');
        console.log(e);
        this.generating = false;
      });
    },
  },
}
</script>

<style scoped>
.shift-header {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 16px; flex-wrap: wrap; gap: 12px;
}
.month-selector { }
.year-selector { display: flex; align-items: center; justify-content: center; gap: 12px; margin-bottom: 8px; }
.year-nav-btn { width: 32px; height: 32px; border-radius: 50%; border: 1.5px solid #d2d6dc; background: #fff; color: #4a5568; font-size: 12px; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.2s; }
.year-nav-btn:hover { background: #049C4C; border-color: #049C4C; color: #fff; }
.year-label { font-size: 20px; font-weight: 700; min-width: 80px; text-align: center; }
.month-btn-group { display: flex; flex-wrap: wrap; gap: 4px; }
.month-btn { padding: 6px 12px; font-size: 13px; font-weight: 600; border: 1.5px solid #d2d6dc; border-radius: 6px; background: #fff; color: #4a5568; cursor: pointer; transition: all 0.15s; min-width: 48px; }
.month-btn:hover { background: #edf2f7; border-color: #a0aec0; }
.month-btn.active { background: #049C4C; color: #fff; border-color: #049C4C; }
.shift-btn-group { display: flex; gap: 6px; }

/* Modal */
.modal-overlay {
  position: fixed; top: 0; left: 0; width: 100%; height: 100%;
  background: rgba(0,0,0,0.4); z-index: 1050;
  display: flex; align-items: center; justify-content: center;
}
.modal-box {
  background: #fff; border-radius: 12px; width: 360px; max-width: 90vw;
  box-shadow: 0 20px 60px rgba(0,0,0,0.2); overflow: hidden;
}
.modal-box-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 18px; border-bottom: 1px solid #edf2f7;
}
.modal-box-title { font-weight: 700; font-size: 14px; margin: 0; }
.badge-status-approved {
  display: inline-block; padding: 2px 8px; border-radius: 4px;
  font-size: 11px; font-weight: 700; color: #fff; background: #3182ce; margin-left: 4px;
}
.badge-status-pending {
  display: inline-block; padding: 2px 8px; border-radius: 4px;
  font-size: 11px; font-weight: 700; color: #fff; background: #ed8936; margin-left: 4px;
}
.modal-box-close { border: none; background: none; font-size: 22px; color: #718096; cursor: pointer; }
.modal-box-body { padding: 18px; }
.modal-box-footer { display: flex; justify-content: flex-end; gap: 8px; padding: 12px 18px; border-top: 1px solid #edf2f7; }

.shift-type-btns { display: flex; gap: 6px; }
.type-btn {
  flex: 1; padding: 10px; border: 1.5px solid #d2d6dc; border-radius: 8px;
  background: #fff; font-size: 14px; font-weight: 600; cursor: pointer; text-align: center; transition: all 0.15s;
}
.type-btn.active { background: #049C4C; border-color: #049C4C; color: #fff; }
.type-btn.half.active { background: #d69e2e; border-color: #d69e2e; }
.type-btn.off.active { background: #718096; border-color: #718096; }
.type-btn.leave.active { background: #ed8936; border-color: #ed8936; }
.type-btn.half-leave.active { background: #38a169; border-color: #38a169; }

.edit-times { display: flex; gap: 12px; }
.edit-times .field-group { flex: 1; }
.edit-times label, .modal-box-body .field-group > label {
  font-size: 12px; font-weight: 600; color: #718096; margin-bottom: 2px; display: block;
}

.location-btns { display: flex; gap: 6px; }
.loc-btn {
  flex: 1; padding: 8px; border: 1.5px solid #d2d6dc; border-radius: 6px;
  background: #fff; font-size: 13px; font-weight: 600; cursor: pointer; text-align: center; transition: all 0.15s;
}
.loc-btn.active { background: #3182ce; border-color: #3182ce; color: #fff; }

.pattern-select-btns { display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 8px; }
.pattern-sel-btn {
  padding: 6px 14px; border: 1.5px solid #d2d6dc; border-radius: 6px;
  background: #fff; font-size: 13px; font-weight: 600; cursor: pointer;
  transition: all 0.15s; color: #4a5568;
}
.pattern-sel-btn:hover { border-color: #049C4C; color: #049C4C; }
.pattern-sel-btn.active { background: #049C4C; border-color: #049C4C; color: #fff; }
</style>

<style>
/* Grid styles (unscoped) */
.shift-table-wrap {
  overflow-x: auto; border: 1px solid #e2e8f0; border-radius: 8px; min-height: 60px;
}
.shift-grid { white-space: nowrap; min-width: max-content; }

.sg-cell {
  border-bottom: 1px solid #edf2f7; border-right: 1px solid #f0f0f0;
  display: flex; align-items: center; justify-content: center;
}
.sg-header {
  background: #f7fafc; border-bottom: 2px solid #e2e8f0;
  padding: 6px 4px; font-size: 11px; font-weight: 600;
}
.sg-sticky {
  position: sticky; left: 0; z-index: 2;
  background: #fff; border-right: 2px solid #e2e8f0 !important;
}
.sg-header.sg-sticky { z-index: 3; background: #f7fafc; }
.sg-name {
  font-weight: 700; font-size: 14px; padding: 6px 10px;
  justify-content: flex-start; flex-direction: column; align-items: flex-start;
}
.sg-name-text { line-height: 1.3; }
.sg-name-badges { display: flex; gap: 3px; margin-top: 2px; }
.sg-badge-off {
  font-size: 10px; font-weight: 700; color: #718096; background: #edf2f7;
  padding: 1px 5px; border-radius: 3px;
}
.sg-badge-half {
  font-size: 10px; font-weight: 700; color: #744210; background: #fefcbf;
  padding: 1px 5px; border-radius: 3px;
}
.sg-badge-leave {
  font-size: 10px; font-weight: 700; color: #c05621; background: #feebc8;
  padding: 1px 5px; border-radius: 3px;
}

.sg-dh { display: flex; flex-direction: column; align-items: center; line-height: 1.3; }
.sg-dn { font-size: 15px; font-weight: 700; }
.sg-dw { font-size: 10px; color: #718096; }
.sg-hol { font-size: 8px; color: #e53e3e; font-weight: 700; }
.sg-sun .sg-dn, .sg-sun { color: #e53e3e !important; }
.sg-sat .sg-dn, .sg-sat { color: #3182ce !important; }
.sg-header.sg-sun { background: #fff5f5 !important; }
.sg-header.sg-sat { background: #ebf8ff !important; }

.sg-data {
  padding: 4px 2px; cursor: pointer; min-width: 56px; min-height: 52px;
  flex-direction: column; transition: background 0.1s;
}
.sg-data:hover { background: #edf2f7 !important; }
.sg-sun-bg { background: #fff5f5; }
.sg-sat-bg { background: #f0f8ff; }
.sg-off-bg { background: #f0f4f8; }
.sg-leave-bg { background: #fef9e7; }
/* 承認待ち: 黄 / 承認済み: 青 */
.sg-leave-pending-bg { background: #fef9e7; }
.sg-leave-approved-bg { background: #ebf5ff; }
.sg-half-bg { background: #fef3cd; }

.sg-shift-content {
  display: flex; flex-direction: column; align-items: center; gap: 1px;
}
.sg-time-main { font-size: 13px; font-weight: 700; color: #2d3748; line-height: 1.3; }
.sg-time-small { font-size: 9px; font-weight: 600; color: #718096; line-height: 1.3; }
.sg-time-sep { font-size: 10px; color: #a0aec0; line-height: 1; }
.sg-time-sep-small { font-size: 7px; }
.sg-time-sub { font-size: 10px; font-weight: 600; color: #718096; line-height: 1.3; }

.sg-type-label {
  font-size: 15px; font-weight: 800; letter-spacing: 0.05em;
}
.sg-type-off { color: #a0aec0; }
.sg-type-paid { color: #d97706; }
.sg-type-paid-pending { color: #d97706; }
.sg-type-paid-approved { color: #2b6cb0; }
.sg-pending-mark {
  display: inline-block;
  margin-left: 3px;
  font-size: 10px;
  font-weight: 700;
  color: #fff;
  background: #ed8936;
  padding: 1px 4px;
  border-radius: 3px;
  vertical-align: middle;
}
.sg-type-half { color: #b45309; font-size: 14px; }
.sg-type-half-leave { color: #38a169; font-size: 13px; }
.sg-half-leave-bg { background: #e6fffa; }

.sg-loc {
  font-size: 9px; font-weight: 700; color: #fff; background: #3182ce;
  padding: 1px 5px; border-radius: 3px; margin-top: 2px; display: inline-block;
  min-height: 15px;
}
.sg-loc-spacer {
  visibility: hidden;
}

.sg-count-label {
  font-size: 11px; font-weight: 700; color: #4a5568;
  background: #f7fafc; border-top: 2px solid #e2e8f0;
  justify-content: flex-start; padding-left: 8px;
}
.sg-req-off {
  border: 2px solid #ed8936 !important;
  position: relative;
}
.sg-req-mark {
  position: absolute;
  top: 1px;
  right: 2px;
  font-size: 7px;
  font-weight: 700;
  color: #fff;
  background: #ed8936;
  padding: 0 3px;
  border-radius: 2px;
  line-height: 1.4;
}

.sg-count {
  font-size: 12px; font-weight: 700; color: #049C4C;
  background: #f7fafc; border-top: 2px solid #e2e8f0; padding: 6px 4px;
}
</style>
